import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX } = FiIcons;

const ArticleModal = ({ isOpen, onClose, onSave, article = null, agencies = [], domains = [] }) => {
  const [formData, setFormData] = useState({
    article_url: '',
    agency_id: '',
    domain_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (article) {
      setFormData({
        article_url: article.article_url || '',
        agency_id: article.agency_id || '',
        domain_id: article.domain_id || ''
      });
    } else {
      setFormData({
        article_url: '',
        agency_id: '',
        domain_id: ''
      });
    }
  }, [article]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateTitleFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Extract title from URL path (remove slashes and hyphens, capitalize words)
      const title = pathname
        .split('/')
        .pop()
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .trim();
      
      return title || 'New Article';
    } catch {
      return 'New Article';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get agency and domain details
      const selectedAgency = agencies.find(agency => agency.id === formData.agency_id);
      const selectedDomain = domains.find(domain => domain.id === formData.domain_id);

      // Generate title from URL
      const generatedTitle = generateTitleFromUrl(formData.article_url);

      const processedData = {
        ...formData,
        // Auto-generate title from URL
        title: article ? article.title : `${generatedTitle} - ${selectedAgency?.name || 'Client'}`,
        // Use agency's price per article
        price: selectedAgency?.price_per_article || 0,
        // Set default status
        status: article ? article.status : 'draft',
        // Set default content
        content: article ? article.content : 'Article content to be written...',
        // Set default keywords based on agency niche
        keywords: selectedAgency?.niche_type || 'content',
        // Set word count to 0 initially
        word_count: article ? article.word_count : 0,
        // No published date initially
        published_at: article ? article.published_at : null
      };

      await onSave(processedData);
      onClose();
    } catch (error) {
      console.error('Error saving article:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {article ? 'Edit Article' : 'Add New Article'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Article URL *
            </label>
            <input
              type="url"
              name="article_url"
              value={formData.article_url}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="https://example.com/article-title"
            />
            <p className="text-xs text-gray-500 mt-1">
              The published article URL
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Agency *
            </label>
            <select
              name="agency_id"
              value={formData.agency_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select Agency</option>
              {agencies.map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name} ({agency.remaining_articles} articles left)
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Choose the client agency for this article
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Publication Domain *
            </label>
            <select
              name="domain_id"
              value={formData.domain_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select Domain</option>
              {domains.map(domain => (
                <option key={domain.id} value={domain.id}>
                  {domain.name} ({domain.url})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Choose where this article will be published
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Auto-Generated Fields:</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• Title: Generated from URL and agency name</p>
              <p>• Price: Uses agency's price per article</p>
              <p>• Status: Set to "Draft" initially</p>
              <p>• Keywords: Based on agency's niche type</p>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (article ? 'Update Article' : 'Create Article')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ArticleModal;